import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DashboardService } from '../service/dashboard.service';
import {
  DashboardFilterRequest,
  DashboardTopResponse,
} from '../service/models/dashboard.service.model';
import { ButtonColorEnum } from 'src/app/shared/buttons/enums/button-color.enum';
import { finalize } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-dashboard-top5-metrics',
  templateUrl: './dashboard-top5-metrics.component.html',
  styleUrls: ['./dashboard-top5-metrics.component.scss'],
})
export class DashboardTop5MetricsComponent {
  @Input() filter!: DashboardFilterRequest;
  @Input() type:
    | 'unresolved-reasons'
    | 'resolutive-reasons'
    | 'worst-attendants'
    | 'best-attendants' = 'unresolved-reasons';
  @Output() isLoadingEmmiter: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  bestAttendantsData: DashboardTopResponse[] = [];
  worstAttendantsData: DashboardTopResponse[] = [];
  resolutiveReasonsData: DashboardTopResponse[] = [];
  unresolvedReasonsData: DashboardTopResponse[] = [];

  ButtonColorEnum = ButtonColorEnum;

  title: string = '';

  loading: boolean = false;
  loadingDownloadTopFive: boolean = false;
  error: boolean = false;

  parseFloat = parseFloat;

  constructor(
    private readonly dashboardService: DashboardService,
    private readonly toastrService: ToastrService
  ) {}

  ngOnInit() {
    this.setTitle();
    this.loadData();
  }

  ngOnChanges() {
    this.loadData();
  }

  setTitle(): void {
    const titlesMap = {
      'best-attendants': 'Atendentes com Maior Resolutividade',
      'worst-attendants': 'Atendentes com Menor Resolutividade',
      'resolutive-reasons': 'Motivos com Maior Resolutividade',
      'unresolved-reasons': 'Motivos com Menor Resolutividade',
    };
    this.title = titlesMap[this.type] || 'Título Desconhecido';
  }

  loadData(): void {
    this.filter.topQuantity = 5;
    switch (this.type) {
      case 'best-attendants':
        this.getBestAttendants();
        break;
      case 'worst-attendants':
        this.getWorstAttendants();
        break;
      case 'resolutive-reasons':
        this.getResolutiveReasons();
        break;
      case 'unresolved-reasons':
        this.getUnresolvedReasons();
        break;
    }
  }

  get metricsData(): DashboardTopResponse[] {
    const dataMap = {
      'best-attendants': this.bestAttendantsData,
      'worst-attendants': this.worstAttendantsData,
      'resolutive-reasons': this.resolutiveReasonsData,
      'unresolved-reasons': this.unresolvedReasonsData,
    };
    return dataMap[this.type] || [];
  }

  errorBestAttendants: boolean = false;
  loadingBestAttendants: boolean = false;

  getBestAttendants(): void {
    this.errorBestAttendants = false;
    this.loadingBestAttendants = true;
    this.dashboardService.getBestAttendants(this.filter).subscribe({
      next: (response) => {
        this.bestAttendantsData = response.map((item) => ({
          name: item.name,
          quantity: item.quantity,
        }));
        this.loadingBestAttendants = false;
      },
      error: () => {
        this.loadingBestAttendants = false;
        this.errorBestAttendants = true;
      },
    });
  }

  errorWorstAttendants: boolean = false;
  loadingWorstAttendants: boolean = false;

  getWorstAttendants(): void {
    console.log('getWorstAttendants', this.filter);
    this.errorWorstAttendants = false;
    this.loadingWorstAttendants = true;
    this.dashboardService.getWorstAttendants(this.filter).subscribe({
      next: (response) => {
        this.worstAttendantsData = response.map((item) => ({
          name: item.name,
          quantity: item.quantity,
        }));
        this.loadingWorstAttendants = false;
      },
      error: () => {
        this.loadingWorstAttendants = false;
        this.errorWorstAttendants = true;
      },
    });
  }

  errorResolutiveReasons: boolean = false;
  loadingResolutiveReasons: boolean = false;

  getResolutiveReasons(): void {
    this.errorResolutiveReasons = false;
    this.loadingResolutiveReasons = true;
    this.dashboardService.getTopResolutiveReasons(this.filter).subscribe({
      next: (response) => {
        this.resolutiveReasonsData = response.map((item) => ({
          name: item.name,
          quantity: parseFloat((item.percentage ?? 0).toFixed(2)),
        }));
        this.loadingResolutiveReasons = false;
      },
      error: () => {
        this.loadingResolutiveReasons = false;
        this.errorResolutiveReasons = true;
      },
    });
  }

  errorUnresolvedReasons: boolean = false;
  loadingUnresolvedReasons: boolean = false;

  getUnresolvedReasons(): void {
    this.errorUnresolvedReasons = false;
    this.loadingUnresolvedReasons = true;
    this.dashboardService.getUnresolvedReasons(this.filter).subscribe({
      next: (response) => {
        console.log(response);
        this.unresolvedReasonsData = response.map((item) => ({
          name: item.name,
          quantity: parseFloat((item.percentage ?? 0).toFixed(2)),
        }));
        this.loadingUnresolvedReasons = false;
      },
      error: () => {
        this.loadingUnresolvedReasons = false;
        this.errorUnresolvedReasons = true;
      },
    });
  }

  downloadTopFive() {
    console.log('downloadTopFive', this.filter);
    if (this.loadingDownloadTopFive) return;

    this.error = false;
    this.loadingDownloadTopFive = true;
    this.filter.topQuantity = 5;

    this.dashboardService
      .downloadTopFive(this.filter)
      .pipe(finalize(() => (this.loadingDownloadTopFive = false)))
      .subscribe({
        next: (data) => {
          if (data.status === 204) {
            this.error = true;
            this.toastrService.warning(
              'Não há resultados para o filtro informado.'
            );
          } else {
            this.handleReportDownloadSuccess(data.body);
          }
        },
        error: () => {
          this.error = true;
          this.toastrService.error(
            'Ocorreu um erro ao gerar a relatório. Tente novamente mais tarde.'
          );
        },
      });
  }

  handleReportDownloadSuccess(reportFile: Blob | null) {
    const fileAnchor = document.createElement('a');
    const data = new Date();
    fileAnchor.download = `Top5-${data
      .getDate()
      .toString()
      .padStart(2, '0')}_${(data.getMonth() + 1)
      .toString()
      .padStart(
        2,
        '0'
      )}_${data.getFullYear()}_${data.getHours()}_${data.getMinutes()}.xlsx`;

    fileAnchor.href = window.URL.createObjectURL(reportFile as Blob);
    fileAnchor.click();
    fileAnchor.hidden = true;
    window.URL.revokeObjectURL(fileAnchor.href);
  }
}
